const CART_ITEM_ORDER_STORAGE_KEY = "cart-drawer-item-order";
function readCartItemOrder() {
  try {
    const stored = sessionStorage.getItem(CART_ITEM_ORDER_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn("[CART DRAWER] Failed to read item order:", err);
    return [];
  }
}
function saveCartItemOrder(keys) {
  try {
    sessionStorage.setItem(CART_ITEM_ORDER_STORAGE_KEY, JSON.stringify(keys));
  } catch (err) {
    console.warn("[CART DRAWER] Failed to save item order:", err);
  }
}

const appliedDiscountCodes = (cart) => {
  const codes = new Set();
  (cart?.discount_codes || []).forEach((entry) => {
    if (entry?.applicable && entry.code) {
      codes.add(entry.code.trim().toLowerCase());
    }
  });
  const collect = (entry) => {
    const application = entry?.discount_application || entry;
    if (application?.type === "discount_code" && application.title) {
      codes.add(application.title.trim().toLowerCase());
    }
  };
  (cart?.cart_level_discount_applications || []).forEach(collect);
  (cart?.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach(collect);
  });
  return [...codes];
};

window.replaceCartItemOrderKey = function (oldKey, newKey) {
  if (!oldKey || !newKey || oldKey === newKey) return;
  const order = readCartItemOrder();
  const index = order.indexOf(oldKey);
  if (index === -1) return;
  if (order.includes(newKey)) {
    // Variant change merged into an existing line — just drop the old key.
    order.splice(index, 1);
  } else {
    order[index] = newKey;
  }
  saveCartItemOrder(order);
};
class CartButton extends HTMLElement {
  connectedCallback() {
    this.clickable =
      this.querySelector("button") || this.querySelector("a") || this;
    this.handleClick = this.handleClick.bind(this);
    this.clickable.addEventListener("click", this.handleClick);
  }
  disconnectedCallback() {
    this.clickable?.removeEventListener("click", this.handleClick);
  }
  handleClick(event) {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent("cart:open"));
  }
}
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector(".cart-drawer-container");
    this.overlay = this.querySelector(".cart-drawer-overlay");
    this.loader = this.querySelector(".cart-loader");
    this.openBtn = this.querySelector(".open-cart-drawer");
    this.closeBtn = this.querySelector(".close-cart-drawer");
    this.isOpen = false;
    this.renderTimeout = null;
    this.isRendering = false;
    this.pendingHideLoader = false;
    this.hideLoaderTimeout = null;
    this.loaderLocked = false;
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.render = this.render.bind(this);
    this.showLoader = this.showLoader.bind(this);
    this.hideLoader = this.hideLoader.bind(this);
    this.handleCartLoaded = this.handleCartLoaded.bind(this);
    this.openBtn?.addEventListener("click", this.open);
    this.closeBtn?.addEventListener("click", this.close);
    this.overlay?.addEventListener("click", this.close);
    window.addEventListener("cart:updated", (event) => {
      // If the add-to-cart call already requested the rendered drawer HTML
      // via the sections API, use it directly instead of fetching it again.
      const prefetchedHTML = event.detail?.sections?.["cart-drawer"];
      this.render(prefetchedHTML);
    });
    window.addEventListener("cart:open", this.open);
    window.addEventListener("cart:loading", () => {
      this.showLoader();
    });
    window.addEventListener("cart:loaded", () => {
      this.handleCartLoaded();
    });
    window.addEventListener("cart:adding", () => {
      this.showLoader();
    });
    this.initVariantDropdowns();
    this.setupCouponDrawer();
    this.applyStableItemOrder();
    this.syncAppliedCoupons();
  }
  applyStableItemOrder(root = this) {
    const list = root.querySelector(".cart-items");
    if (!list) return;
    const items = Array.from(list.querySelectorAll(".cart-item[data-item-key]"));
    if (!items.length) return;
    const currentKeys = items.map((li) => li.dataset.itemKey);
    const saved = readCartItemOrder().filter((key) => currentKeys.includes(key));
    let order = saved.concat(currentKeys.filter((key) => !saved.includes(key)));
    // Keep freebie lines pinned last, matching the Liquid render order.
    const freebieVariantId = window.FREEBIE_CONFIG?.variantId
      ? String(window.FREEBIE_CONFIG.variantId)
      : null;
    if (freebieVariantId) {
      const isFreebieKey = (key) => key.split(":")[0] === freebieVariantId;
      order = order
        .filter((key) => !isFreebieKey(key))
        .concat(order.filter(isFreebieKey));
    }
    order.forEach((key) => {
      const li = items.find((el) => el.dataset.itemKey === key);
      if (li) list.appendChild(li);
    });
    saveCartItemOrder(order);
  }
  open() {
    if (!this.drawer || this.isOpen) return;
    this.drawer.classList.add("open");
    this.overlay?.classList.add("open");
    this.isOpen = true;
  }
  close() {
    if (!this.drawer || !this.isOpen) return;
    this.drawer.classList.remove("open");
    this.overlay?.classList.remove("open");
    this.hideLoader();
    this.isOpen = false;
  }
  showLoader() {
    if (!this.loader) {
      return;
    }
    this.loader.classList.add("show");
    this.loaderLocked = true;
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }
  }
  handleCartLoaded() {
    if (this.isRendering || this.renderTimeout) {
      this.pendingHideLoader = true;
      return;
    }
    if (this.loaderLocked) {
      this.pendingHideLoader = true;
      this.loaderLocked = false;
      return;
    }
    this.loaderLocked = false;
    this.hideLoader();
  }
  hideLoader() {
    if (!this.loader) return;
    if (this.loaderLocked || this.isRendering) {
      return;
    }
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }
    if (this.hideLoaderTimeout) {
      clearTimeout(this.hideLoaderTimeout);
      this.hideLoaderTimeout = null;
    }
    this.loader.style.opacity = "";
    this.loader.classList.remove("show");
    this.pendingHideLoader = false;
  }
  async render(prefetchedHTML) {
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    this.renderTimeout = setTimeout(async () => {
      this.renderTimeout = null;
      await this._performRender(prefetchedHTML);
    }, 50);
  }
  async _performRender(prefetchedHTML) {
    if (this.isRendering) {
      return;
    }
    this.isRendering = true;
    const hadPendingHide = this.pendingHideLoader;
    this.loaderLocked = true;
    try {
      if (this.loaderTimeout) {
        clearTimeout(this.loaderTimeout);
        this.loaderTimeout = null;
      }
      if (this.hideLoaderTimeout) {
        clearTimeout(this.hideLoaderTimeout);
        this.hideLoaderTimeout = null;
      }
      if (this.loader) {
        this.loader.classList.add("show");
      }
      let html = prefetchedHTML;
      if (!html) {
        const res = await fetch(
          window.location.pathname + "?sections=cart-drawer"
        );
        const data = await res.json();
        html = data["cart-drawer"];
      }
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newDrawer = doc.querySelector(".cart-drawer-container");
      if (!newDrawer) {
        this.isRendering = false;
        this.hideLoader();
        return;
      }
      const wasOpen = this.isOpen;
      const prevScrollArea = this.querySelector(".cart-drawer-scroll-area");
      const prevScrollTop = prevScrollArea ? prevScrollArea.scrollTop : 0;
      this.applyStableItemOrder(newDrawer);
      this.drawer.innerHTML = newDrawer.innerHTML;
      if (window.gokwikCheckoutReady) {
        this.querySelectorAll(".gokwik-checkout button").forEach((button) => {
          button.disabled = false;
          button.classList.remove("disabled");
        });
      }
      this.overlay = this.querySelector(".cart-drawer-overlay");
      this.loader = this.querySelector(".cart-loader");
      this.openBtn = this.querySelector(".open-cart-drawer");
      this.closeBtn = this.querySelector(".close-cart-drawer");
      if (this.loader) {
        this.loader.style.transition = "none";
        this.loader.classList.add("show");
        this.loader.style.opacity = "1";
        requestAnimationFrame(() => {
          if (this.loader) this.loader.style.transition = "";
        });
      }
      const newScrollArea = this.querySelector(".cart-drawer-scroll-area");
      if (newScrollArea) {
        newScrollArea.scrollTop = prevScrollTop;
      }
      this.openBtn?.addEventListener("click", this.open);
      this.closeBtn?.addEventListener("click", this.close);
      this.overlay?.addEventListener("click", this.close);
      this.syncAppliedCoupons();
      setTimeout(() => this.initVariantDropdowns(), 50);
      if (typeof initializeProductCards === "function") {
        setTimeout(() => initializeProductCards(), 100);
      }
      if (typeof initializeCartRecommendations === "function") {
        setTimeout(() => initializeCartRecommendations(), 150);
      }
      if (typeof initializeLazyVideos === "function") {
        setTimeout(() => initializeLazyVideos(this.drawer), 200);
      }
      if (wasOpen) {
        this.drawer.classList.add("open");
        this.overlay?.classList.add("open");
        this.isOpen = true;
      }
    } catch (err) {
      console.error("[CART DRAWER] Render error:", err);
    } finally {
      this.isRendering = false;
      this.loaderLocked = false;

      if (hadPendingHide || this.pendingHideLoader) {
        this.pendingHideLoader = false;
        this.hideLoaderTimeout = setTimeout(() => {
          if (!this.isRendering && !this.loaderLocked) {
            this.hideLoader();
          }
        }, 400);
      } else {
        this.hideLoaderTimeout = setTimeout(() => {
          if (
            !this.isRendering &&
            !this.loaderLocked &&
            !this.pendingHideLoader
          ) {
            this.hideLoader();
          }
        }, 400);
      }
    }
  }
  disconnectedCallback() {
    window.removeEventListener("cart:updated", this.render);
    window.removeEventListener("cart:open", this.open);
    window.removeEventListener("cart:loading", this.showLoader);
    window.removeEventListener("cart:loaded", this.handleCartLoaded);
    window.removeEventListener("cart:adding", this.showLoader);
    if (this._pageshowHandler) {
      window.removeEventListener("pageshow", this._pageshowHandler);
    }
    // Clean up timeouts
    if (this.renderTimeout) {
      clearTimeout(this.renderTimeout);
    }
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
    }
    if (this.hideLoaderTimeout) {
      clearTimeout(this.hideLoaderTimeout);
    }
  }
  initVariantDropdowns() {
    const dropdownTriggers = this.querySelectorAll("[data-dropdown-trigger]");
    dropdownTriggers.forEach((trigger) => {
      if (trigger._dropdownClickHandler) {
        trigger.removeEventListener("click", trigger._dropdownClickHandler);
      }
      const handler = function (e) {
        e.preventDefault();
        e.stopPropagation();
        const trig = e.currentTarget;
        const wrapper = trig.closest(".cart-variant-dropdown-wrapper");
        if (!wrapper) return;
        const dropdown = wrapper.querySelector(".variant-dropdown-menu");
        if (!dropdown) return;
        trig
          .closest("cart-drawer")
          ?.querySelectorAll(".variant-dropdown-menu")
          .forEach((menu) => {
            if (menu !== dropdown) {
              menu.classList.remove("show");
              const otherTrigger = menu
                .closest(".cart-variant-dropdown-wrapper")
                ?.querySelector("[data-dropdown-trigger]");
              if (otherTrigger) otherTrigger.classList.remove("active");
            }
          });
        const isShowing = dropdown.classList.contains("show");
        dropdown.classList.toggle("show");
        trig.classList.toggle("active", !isShowing);
      };
      trigger._dropdownClickHandler = handler;
      trigger.addEventListener("click", handler);
    });
    if (this._drawerClickHandler) {
      this.drawer.removeEventListener("click", this._drawerClickHandler);
    }
    this._drawerClickHandler = (e) => {
      if (!e.target.closest(".cart-variant-dropdown-wrapper")) {
        this.querySelectorAll(".variant-dropdown-menu").forEach((menu) => {
          menu.classList.remove("show");
          const trigger = menu
            .closest(".cart-variant-dropdown-wrapper")
            ?.querySelector("[data-dropdown-trigger]");
          if (trigger) trigger.classList.remove("active");
        });
      }
    };
    this.drawer.addEventListener("click", this._drawerClickHandler);
  }
  setupCouponDrawer() {
    if (this._couponSetupDone) return;
    this._couponSetupDone = true;
    this.addEventListener("click", (event) => {
      const { target } = event;
      if (target.closest("#openCouponDrawer")) {
        this.openCouponDrawer();
        return;
      }
      if (
        target.closest("#closeCouponDrawer") ||
        target.closest("#cartCouponOverlay")
      ) {
        this.closeCouponDrawer();
        return;
      }
      const removeCouponBtn = target.closest("[data-remove-coupon]");
      if (removeCouponBtn) {
        this.removeDiscountCode(removeCouponBtn);
        return;
      }
      const cardApplyBtn = target.closest("[data-apply-coupon-card]");
      if (cardApplyBtn) {
        if (cardApplyBtn.classList.contains("is-applied")) {
          this.removeDiscountCode(cardApplyBtn);
        } else {
          this.applyDiscountCode(cardApplyBtn.dataset.coupon || "", cardApplyBtn);
        }
        return;
      }
      const inputApplyBtn = target.closest("[data-apply-coupon-input]");
      if (inputApplyBtn) {
        const input = document.getElementById("cartCouponInput");
        this.applyDiscountCode(input ? input.value.trim() : "", inputApplyBtn);
      }
    });
    this.addEventListener("input", (event) => {
      if (event.target.id === "cartCouponInput") {
        event.target.classList.remove("is-invalid");
        this.setCouponMessage("");
        const applyBtn = document.getElementById("applyCouponManual");
        if (applyBtn) {
          applyBtn.hidden = event.target.value.trim() === "";
          applyBtn.disabled = false;
          applyBtn.classList.remove("is-applied");
          applyBtn.textContent = "APPLY";
        }
      }
    });

    this.addEventListener("keydown", (event) => {
      if (event.target.id === "cartCouponInput" && event.key === "Enter") {
        event.preventDefault();
        const applyBtn = document.getElementById("applyCouponManual");
        this.applyDiscountCode(event.target.value.trim(), applyBtn);
      }
    });

    this._pageshowHandler = (event) => {
      if (event.persisted) this.render();
    };
    window.addEventListener("pageshow", this._pageshowHandler);
  }

  async syncAppliedCoupons() {
    try {
      const cart = await fetch("/cart.js").then((res) => res.json());
      const appliedCodes = appliedDiscountCodes(cart);
      this.querySelectorAll("[data-apply-coupon-card]").forEach((btn) => {
        const code = (btn.dataset.coupon || "").trim().toLowerCase();
        const isApplied = code !== "" && appliedCodes.includes(code);
        btn.disabled = false;
        btn.classList.toggle("is-applied", isApplied);
        btn.textContent = isApplied ? "REMOVE" : "APPLY";
      });
      this.querySelectorAll("[data-remove-coupon]").forEach((btn) => {
        btn.hidden = appliedCodes.length === 0;
      });
    } catch (err) {
      console.warn("[CART DRAWER] Failed to sync applied coupons:", err);
    }
  }
  setCouponMessage(text) {
    const message = document.getElementById("cartCouponMessage");
    if (!message) return;
    message.textContent = text || "";
    message.hidden = !text;
  }

  async applyDiscountCode(code, btn) {
    if (!btn || btn.disabled) return;

    const input = document.getElementById("cartCouponInput");
    code = (code || "").trim();
    if (!code) {
      this.showBtnFeedback(btn, "Enter a code");
      input?.classList.add("is-invalid");
      input?.focus();
      return;
    }

    const originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = "Applying...";
    this.setCouponMessage("");

    let beforeCart = null;
    try {
      beforeCart = await fetch("/cart.js").then((res) => res.json());
    } catch (err) {
      console.warn("[CART DRAWER] Failed to fetch cart before applying discount:", err);
    }

    const previousCodes = appliedDiscountCodes(beforeCart);
    if (previousCodes.includes(code.toLowerCase())) {
      btn.innerHTML = originalHTML;
      btn.disabled = false;
      input?.classList.remove("is-invalid");
      this.showBtnFeedback(btn, "Already applied");
      return;
    }

    try {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: code }),
      });
      if (!res.ok) throw new Error("Failed to apply discount");
      const cart = await res.json();
      const applied = appliedDiscountCodes(cart).includes(code.toLowerCase());
      if (applied) {
        input?.classList.remove("is-invalid");
        if (input) input.value = "";
        document.getElementById("applyCouponManual")?.setAttribute("hidden", "");
        this.closeCouponDrawer();
        window.dispatchEvent(new CustomEvent("cart:updated"));
        return;
      }
      if (previousCodes.length) {
        try {
          await fetch("/cart/update.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ discount: previousCodes[0] }),
          });
        } catch (restoreErr) {
          console.warn("[CART DRAWER] Failed to restore previous discount:", restoreErr);
        }
      }
      // Shopify only reports "not applicable" here — it covers unknown codes and codes that don't fit this cart
      btn.innerHTML = originalHTML;
      input?.classList.add("is-invalid");
      this.setCouponMessage(`${code.toUpperCase()} is not applicable for the selected products.`);
    } catch (err) {
      console.error("[CART DRAWER] Failed to apply discount:", err);
      btn.textContent = "Try again";
    } finally {
      setTimeout(() => {
        if (btn.isConnected) {
          btn.innerHTML = originalHTML;
          btn.disabled = false;
        }
      }, 1500);
    }
  }
  async removeDiscountCode(btn) {
    if (!btn || btn.disabled) return;
    const originalHTML = btn.innerHTML;
    btn.disabled = true;
    btn.textContent = "Removing...";
    try {
      const res = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ discount: "" }),
      });
      if (!res.ok) throw new Error("Failed to remove discount");
      window.dispatchEvent(new CustomEvent("cart:updated"));
    } catch (err) {
      console.error("[CART DRAWER] Failed to remove discount:", err);
      btn.innerHTML = originalHTML;
      btn.disabled = false;
      this.showBtnFeedback(btn, "Try again");
    }
  }
  showBtnFeedback(btn, text) {
    const origHTML = btn.innerHTML;
    btn.textContent = text;
    setTimeout(() => {
      btn.innerHTML = origHTML;
    }, 1500);
  }
  updateMaxSavings(display) {
    const el = document.getElementById("cartCouponMaxSavings");
    if (el && display) el.textContent = display;
  }
  openCouponDrawer() {
    const drawer = document.getElementById("cartCouponDrawer");
    const overlay = document.getElementById("cartCouponOverlay");
    if (drawer) {
      drawer.classList.add("open");
      drawer.setAttribute("aria-hidden", "false");
    }
    if (overlay) overlay.classList.add("open");
    this.calculateMaxSavings();
  }
  closeCouponDrawer() {
    const drawer = document.getElementById("cartCouponDrawer");
    const overlay = document.getElementById("cartCouponOverlay");
    if (drawer) {
      drawer.classList.remove("open");
      drawer.setAttribute("aria-hidden", "true");
    }
    if (overlay) overlay.classList.remove("open");
    const input = document.getElementById("cartCouponInput");
    if (input) {
      input.value = "";
      input.classList.remove("is-invalid");
    }
    this.setCouponMessage("");
    const applyBtn = document.getElementById("applyCouponManual");
    if (applyBtn) {
      applyBtn.hidden = true;
      applyBtn.disabled = false;
      applyBtn.classList.remove("is-applied");
      applyBtn.textContent = "APPLY";
    }
  }
  calculateMaxSavings() {
    let max = 0;
    let maxCard = null;
    this.querySelectorAll(".cart-coupon-card").forEach((card) => {
      const val = parseFloat(card.dataset.discountValue || 0);
      if (val > max) {
        max = val;
        maxCard = card;
      }
    });
    this.updateMaxSavings(maxCard?.dataset.discountDisplay);
  }
}
customElements.define("open-cart-drawer", CartButton);
customElements.define("cart-drawer", CartDrawer);
